import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import { PaymentDeviceRentalRequestStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { MANAGEMENT_DEVICE_REQUESTS_URL, formatDeviceDate, formatPickupDay, isAwaitingPickup } from '@/helpers/paymentDevices';
import { RequestStatusChip } from './DeviceStatusChip';
import { useDeviceDialogs, useRequestDialogs } from './useDeviceDialogs';
import RequestBlade from './RequestBlade';

export type RequestsTabKind = 'pending' | 'approved' | 'closed';

const requestSearch: FilterFn<PaymentDeviceRentalRequestDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const query = String(value).toLowerCase();
  const { id, fundId, fundName, submittedByEmail, submittedByName, notes, rental } = row.original;
  return [String(id), String(fundId), fundName, submittedByEmail, submittedByName, notes, rental?.deviceNumber]
    .some((field) => field?.toLowerCase().includes(query));
};

const formatTimestamp = (value: string | null) => (value ? new Date(value).toLocaleDateString('he-IL') : '');

interface RequestsTabProps {
  kind: RequestsTabKind;
  /** A request to open on arrival, e.g. one that holds a device, picked from the approve dialog. */
  initialRequestId?: number | null;
  onOpenRequest?: (requestId: number) => void;
}

/** One of the three request tabs. Pending requests are oldest first - the order they should be handled in. */
const RequestsTab = ({ kind, initialRequestId = null, onOpenRequest }: RequestsTabProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(`table.density.paymentDeviceRequests.${kind}`, 'normal');

  const { data: requests, isLoading } = useGetAuthApi<PaymentDeviceRentalRequestDtoIn[]>(`${MANAGEMENT_DEVICE_REQUESTS_URL}?tab=${kind}`);
  const { approve, reject, collect, reopen, dialogs } = useRequestDialogs(onOpenRequest);
  const deviceDialogs = useDeviceDialogs();
  const [bladeRequestId, setBladeRequestId] = useState<number | null>(initialRequestId);
  // Looked up in the list, not kept as a copy, so the blade shows the request as the list
  // re-reads it after an approval or a hand-over.
  const bladeRequest = requests?.find((request) => request.id === bladeRequestId) ?? null;

  const columns: ColumnDef<PaymentDeviceRentalRequestDtoIn>[] = [
    {
      id: 'id',
      header: Col(t('paymentDevices.columns.requestId')),
      accessorKey: 'id',
      enableSorting: true,
      meta: { className: 'min-w-[80px] max-w-[90px]' },
    },
    {
      id: 'fund',
      header: Col(t('paymentDevices.columns.fund')),
      accessorKey: 'fundId',
      enableSorting: true,
      cell: ({ row }) => <span>{row.original.fundId} | {row.original.fundName ?? ''}</span>,
      meta: { className: 'min-w-[140px]' },
    },
    {
      id: 'submittedBy',
      header: Col(t('paymentDevices.columns.submittedBy')),
      accessorKey: 'submittedAt',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="truncate">{row.original.submittedByName || row.original.submittedByEmail}</span>
          <span className="body-sm-regular text-(--font-tertiary)">{formatTimestamp(row.original.submittedAt)}</span>
        </span>
      ),
      meta: { className: 'min-w-[130px]' },
    },
    // Start and expected return share one column, a line each, to leave room for the rest.
    {
      id: 'period',
      header: Col(t('paymentDevices.columns.period')),
      accessorKey: 'startDate',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span>{formatDeviceDate(row.original.startDate)}</span>
          <span className="body-sm-regular text-(--font-body-2)">
            {t('paymentDevices.columns.until', { date: formatDeviceDate(row.original.expectedReturnDate) })}
          </span>
        </span>
      ),
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'pickupArea',
      header: Col(t('paymentDevices.columns.pickupArea')),
      accessorKey: 'pickupArea',
      enableSorting: true,
      cell: ({ row }) => t(`paymentDevices.pickupArea.${row.original.pickupArea}`),
      meta: { className: 'min-w-[175px]' },
    },
    ...(kind === 'pending'
      ? [{
        // The coordinator's notes matter while deciding; once decided they are in the request blade.
        id: 'notes',
        header: Col(t('paymentDevices.columns.notes')),
        accessorKey: 'notes',
        enableSorting: false,
        cell: ({ row }) => <span className="line-clamp-2 whitespace-normal">{row.original.notes || '—'}</span>,
        meta: { className: 'min-w-[120px]' },
      } as ColumnDef<PaymentDeviceRentalRequestDtoIn>, {
        id: 'actions',
        header: Col(t('paymentDevices.columns.actions')),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex gap-2 whitespace-nowrap [&>button]:shrink-0">
            <Button variant="outline" className="!bg-(--font-primary) !text-white !border-(--font-primary)" onClick={(e) => { e.stopPropagation(); approve(row.original); }}>
              {t('paymentDevices.actions.approve')}
            </Button>
            <Button variant="outline" className="!text-red-500 !border-red-300" onClick={(e) => { e.stopPropagation(); reject(row.original); }}>
              {t('paymentDevices.actions.reject')}
            </Button>
          </div>
        ),
        meta: { className: 'w-[180px] min-w-[180px] max-w-[180px]', lockLayout: true },
      } as ColumnDef<PaymentDeviceRentalRequestDtoIn>]
      : [
        kind === 'approved'
          // Every row here is approved, so the device it got stands where the status would - or, while
          // the fund has not come yet, the last day the device is held and the hand-over itself.
          ? {
            id: 'device',
            header: Col(t('paymentDevices.columns.device')),
            enableSorting: false,
            cell: ({ row }) => {
              const request = row.original;
              const rental = request.rental;
              if (rental) {
                return (
                  <span className="flex flex-col">
                    <bdi className="body-sm-semibold truncate">{rental.deviceNumber}</bdi>
                    <span className="body-sm-regular text-(--font-tertiary)">{t(`paymentDevices.rentalStatus.${rental.status}`)}</span>
                  </span>
                );
              }
              return (
                <span className="flex flex-col">
                  <span className="body-sm-semibold">{t('paymentDevices.requestBlade.held')}</span>
                  {request.holdUntil && (
                    <span className="body-sm-regular text-(--font-tertiary)">
                      {t('paymentDevices.columns.until', { date: formatPickupDay(request.holdUntil, false) })}
                    </span>
                  )}
                </span>
              );
            },
            meta: { className: 'min-w-[210px] whitespace-nowrap' },
          }
          // Rejected and cancelled share this tab: the status, with the reason under it.
          : {
            id: 'status',
            header: Col(t('paymentDevices.columns.statusAndReason')),
            accessorKey: 'status',
            enableSorting: true,
            cell: ({ row }) => {
              const request = row.original;
              const reason = request.status === PaymentDeviceRentalRequestStatus.Rejected && request.rejectionReason
                ? t(`paymentDevices.rejectionReason.${request.rejectionReason}`)
                : null;
              const expired = request.expiredAt ? t('paymentDevices.requestBlade.expired', { day: formatPickupDay(request.holdUntil) }) : null;
              const detail = [expired, reason, request.reviewNote].filter(Boolean).join(' · ');
              return (
                <span className="flex flex-col items-start gap-0.5">
                  <RequestStatusChip status={request.status} />
                  {detail && <span className="body-sm-regular text-(--font-body-2) truncate max-w-full" title={detail}>{detail}</span>}
                </span>
              );
            },
            meta: { className: 'min-w-[200px]' },
          },
        {
          id: 'reviewedAt',
          header: Col(t('paymentDevices.columns.reviewedAt')),
          accessorKey: 'reviewedAt',
          enableSorting: true,
          cell: ({ row }) => formatTimestamp(row.original.reviewedAt) || '—',
          meta: { className: 'min-w-[100px]' },
        },
        // The one thing left to do on a decided request: hand the held device over, or bring a
        // cancelled request back to pending.
        {
          id: 'actions',
          header: Col(t('paymentDevices.columns.actions')),
          enableSorting: false,
          cell: ({ row }) => {
            const request = row.original;
            if (isAwaitingPickup(request)) {
              return (
                <Button variant="outline" className="whitespace-nowrap" onClick={(e) => { e.stopPropagation(); collect(request); }}>
                  {t('paymentDevices.actions.handOver')}
                </Button>
              );
            }
            if (request.status === PaymentDeviceRentalRequestStatus.Cancelled) {
              return (
                <Button variant="outline" className="whitespace-nowrap" onClick={(e) => { e.stopPropagation(); reopen(request); }}>
                  {t('paymentDevices.actions.reopen')}
                </Button>
              );
            }
            return null;
          },
          meta: { className: 'w-[150px] min-w-[150px] max-w-[150px]', lockLayout: true },
        },
      ] as ColumnDef<PaymentDeviceRentalRequestDtoIn>[]),
  ];

  return (
    <div>
      <DataTable
        title={t(`paymentDevices.tabs.${kind}`)}
        data={requests ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={<SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="paymentDevices.searchRequests" />}
        emptyStateContent={{ title: t('paymentDevices.emptyRequests.title'), description: t('paymentDevices.emptyRequests.description') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={requestSearch}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey={`paymentDeviceRequests.${kind}`}
        twoLineRows
        getRowId={(request) => String(request.id)}
        handleRowClick={(request) => setBladeRequestId(request.id)}
      />
      {bladeRequest && (
        <RequestBlade
          request={bladeRequest}
          onClose={() => setBladeRequestId(null)}
          onApprove={approve}
          onReject={reject}
          onCollect={collect}
          onReopen={reopen}
          onRentalAction={deviceDialogs.runOnTarget}
        />
      )}
      {dialogs}
      {deviceDialogs.dialogs}
    </div>
  );
};

export default RequestsTab;
